/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes */
/*This file does lower level voltage range calculations that are needed per stage.  It should really be turned into functions rather 
  than an object and combined with utils\voltage-range, which currently duplicates some of the same functionality.
  See documentation at the top of \results\sub-results\voltage-range.js for more details
*/

import EmberObject, { computed, set } from '@ember/object';

import OpAmpSelection from '../../../component-selector/op-amp-selection';
import * as CommonUtils from '../../../../utils/common';

export default class VoltageRangeComputationsPerStage extends EmberObject {
  vsPlus;
  vsMinus;
  circuitResult;
  previousStageOutputMagnitude;
  worstCaseMagnitude; //only used for worst case tolerance
  whoAmI = 'app/models/results/sub-results/voltage-range/voltage-range-computations-per-stage.js';
  frequencyArray;
  circuit;
  filter;
  stopbandFrequency;
  stageFp;
  opAmp;
  opAmpSource;
  init() {
    //Dummy code to allow setting a breakpoint during init.  This allows us to find out what instantiated the object (by looking at the call stack.)
    //var whoCreatedMe = "";
    super.init();
    if (!CommonUtils.isNullOrUndefined(this.circuitResult)) {
      set(this, 'frequencyArray', this.circuitResult.frequencyArray);
      set(this, 'circuit', this.circuitResult.circuit);
      set(this, 'filter', this.circuitResult.actualStage.targetStage.filter);
      set(this, 'stopbandFrequency', this.circuitResult.actualStage.stopbandFrequency);
      set(this, 'stageFp', this.circuitResult.actualStage.targetStage.stageFp);
      set(this, 'opAmp', this.circuitResult.actualStage.opAmp);
      set(this, 'opAmpSource', this.circuitResult.actualStage.opAmpSource);
    }
  }

  //The magnitude waveform coming into the stage (i.e. output of previous stages)
  @computed('previousStageOutputMagnitude', '_maxFrequency', 'frequencyArray') get _inputMagnitudeArray() {
    var inputMagnitudeArray = [];
    var previousStageOutputMagnitude = this.previousStageOutputMagnitude;
    var frequencyArray = this.frequencyArray;
    if (previousStageOutputMagnitude === null) {
      inputMagnitudeArray = frequencyArray.map(() => 1);
    } else {
      inputMagnitudeArray = previousStageOutputMagnitude;
    }
    return inputMagnitudeArray;
  }

  //The magnitude waveform coming out of the stage (output of previous stages * this stage's transfer function).
  @computed(
    '_inputMagnitudeArray',
    '_maxFrequency',
    'circuitResult.magnitude',
    'frequencyArray',
    'inputMagnitudeArray',
    'worstCaseMagnitude',
  )
  get outputMagnitudeArray() {
    var inputMagnitudeArray = this._inputMagnitudeArray;
    var maxFrequency = this._maxFrequency;
    var frequencyArray = this.frequencyArray;
    var worstCaseMagnitude = this.worstCaseMagnitude;
    var stageTransferFunction = worstCaseMagnitude !== null ? worstCaseMagnitude : this.circuitResult.magnitude;
    var outputMagnitudeArray = inputMagnitudeArray.map((inputMagnitude, index) => {
      return inputMagnitude * stageTransferFunction[index];
    });
    outputMagnitudeArray = this._blankOutHighFrequencies(maxFrequency, frequencyArray, outputMagnitudeArray);
    return outputMagnitudeArray;
  }

  //If a section of the waveform is in the stopband area, we don't care if that waveform section gets clipped,
  //so set that section of the waveform to zero when we're checking our limits
  _blankOutHighFrequencies(maxFrequency, frequencyArray, magnitudeArray) {
    var blankedArray = [];
    for (var i = 0; i < magnitudeArray.length; i++) {
      if (frequencyArray[i] <= maxFrequency) {
        blankedArray[i] = magnitudeArray[i];
      } else {
        blankedArray[i] = 0;
      }
    }
    return blankedArray;
  }

  //max frequency above which we blank out the magnitudes, since we don't care about clipping above that point
  @computed('filter', 'stopbandFrequency', 'stageFp', 'frequencyArray') get _maxFrequency() {
    var filter = this.filter;
    var stopbandFrequency = this.stopbandFrequency;
    var stageFp = this.stageFp;
    var frequencyArray = this.frequencyArray;

    var result = filter.getMaxFrequency(stopbandFrequency, frequencyArray, stageFp);
    return result;
  }

  @computed('vsPlus', 'vsMinus') get refV() {
    var vsMinus = this.vsMinus;
    var vsPlus = this.vsPlus;
    var result = 0;

    if (vsMinus === 0) {
      result = vsPlus / 2;
    }

    return result;
  }

  //op amp limits section
  @computed('opAmp', 'opAmpSource') get _opAmp() {
    var opAmpName = this.opAmp;
    var opAmpData = this.opAmpSource.getOpAmp(opAmpName);
    //convert from string storage into numerics
    var opAmp = OpAmpSelection.create({ data: opAmpData });
    return opAmp;
  }

  @computed('vsPlus', '_opAmp.inHeadroomPlus') get maxInput() {
    var vsPlus = this.vsPlus;
    var inHeadroomPlus = this._opAmp.inHeadroomPlus;
    var result = vsPlus - inHeadroomPlus;
    return result;
  }

  @computed('vsMinus', '_opAmp.inHeadroomMinus') get minInput() {
    var vsMinus = this.vsMinus;
    var inHeadroomMinus = this._opAmp.inHeadroomMinus;
    var result = vsMinus + inHeadroomMinus;
    return result;
  }

  @computed('vsPlus', '_opAmp.outHeadroomPlus') get maxOutput() {
    var vsPlus = this.vsPlus;
    var outHeadroomPlus = this._opAmp.outHeadroomPlus;
    var result = vsPlus - outHeadroomPlus;
    return result;
  }

  @computed('vsMinus', '_opAmp.outHeadroomMinus') get minOutput() {
    var vsMinus = this.vsMinus;
    var outHeadroomMinus = this._opAmp.outHeadroomMinus;
    var result = vsMinus + outHeadroomMinus;
    return result;
  }

  @computed('refV', 'maxInput', 'minInput') get _inputRoom() {
    var refV = this.refV;
    var maxInput = this.maxInput;
    var minInput = this.minInput;
    var posInRoom = maxInput - refV;
    var negInRoom = refV - minInput;
    var result = Math.min(posInRoom, negInRoom);
    return result;
  }

  @computed('refV', 'maxOutput', 'minOutput') get _outputRoom() {
    var refV = this.refV;
    var maxOutput = this.maxOutput;
    var minOutput = this.minOutput;
    var posOutRoom = maxOutput - refV;
    var negOutRoom = refV - minOutput;
    var result = Math.min(posOutRoom, negOutRoom);
    return result;
  }

  @computed('outputMagnitudeArray', 'frequencyArray') get _slewRates() {
    var outputMagnitudeArray = this.outputMagnitudeArray;
    var frequencyArray = this.frequencyArray;

    var ONE_MILLION = 1000000;
    var result = [];

    for (var i = 0; i < outputMagnitudeArray.length; i++) {
      result.push((outputMagnitudeArray[i] * frequencyArray[i] * 2 * Math.PI) / ONE_MILLION);
    }

    return result;
  }

  //largest magnitude point in the input magnitude
  @computed('_inputMagnitudeArray') get maxSignalInput() {
    var _inputMagnitudeArray = this._inputMagnitudeArray;
    var result = Math.max.apply(Math, _inputMagnitudeArray);
    return result;
  }
  //largest magnitude point in the output magnitude
  @computed('outputMagnitudeArray') get maxSignalOutput() {
    var outputMagnitudeArray = this.outputMagnitudeArray;
    var result = Math.max.apply(Math, outputMagnitudeArray);
    return result;
  }

  //fastest slewing point in the output magnitude
  @computed('_slewRates') get maxSignalSlewRate() {
    var _slewRates = this._slewRates;
    var result = Math.max.apply(Math, _slewRates);
    return result;
  }
  //The frequency of the fastest slewing output magnitude point.  We calculate here because we report on the screen to the user.
  @computed('_slewRates', 'maxSignalSlewRate', 'frequencyArray') get maxSignalSlewRateFrequency() {
    var _slewRates = this._slewRates;
    var maxSignalSlewRate = this.maxSignalSlewRate;
    var frequencyArray = this.frequencyArray;
    var indexForMaxSlewRate = _slewRates.indexOf(maxSignalSlewRate);
    var result = frequencyArray[indexForMaxSlewRate];
    return result;
  }

  //The frequency of the biggest input magnitude point.  We calculate here because we report on the screen to the user.
  @computed('_inputMagnitudeArray', 'maxSignalInput', 'frequencyArray') get maxSignalInputFrequency() {
    var _inputMagnitudeArray = this._inputMagnitudeArray;
    var maxSignalInput = this.maxSignalInput;
    var frequencyArray = this.frequencyArray;
    var index = _inputMagnitudeArray.indexOf(maxSignalInput);
    var result = frequencyArray[index];
    return result;
  }
  //The frequency of the biggest output magnitude point.  We calculate here because we report on the screen to the user.
  @computed('outputMagnitudeArray', 'maxSignalOutput', 'frequencyArray') get maxSignalOutputFrequency() {
    var outputMagnitudeArray = this.outputMagnitudeArray;
    var maxSignalOutput = this.maxSignalOutput;
    var frequencyArray = this.frequencyArray;
    var index = outputMagnitudeArray.indexOf(maxSignalOutput);
    var result = frequencyArray[index];
    return result;
  }

  //multiplier section
  @computed('_inputRoom', 'maxSignalInput') get multiplierSignalInput() {
    var _inputRoom = this._inputRoom;
    var maxSignalInput = this.maxSignalInput;
    var result = _inputRoom / maxSignalInput;
    return result;
  }

  @computed('_outputRoom', 'maxSignalOutput') get multiplierSignalOutput() {
    var _outputRoom = this._outputRoom;
    var maxSignalOutput = this.maxSignalOutput;
    var result = _outputRoom / maxSignalOutput;
    return result;
  }

  @computed('_opAmp.slewRate', 'maxSignalSlewRate') get multiplierSignalSlewRate() {
    var maxSignalSlewRate = this.maxSignalSlewRate;
    var slewRate = this._opAmp.slewRate;
    var result = slewRate / maxSignalSlewRate;
    return result;
  }
}
